import React, { useContext } from "react";
import { styled } from "@linaria/react";
import { Link } from "../Link";
import type {
  ClassNetwork,
  Declaration,
  FieldNetwork,
  NetworkOverride,
  SchemaClass,
  SchemaField,
} from "../../data/types";
import { findDeclarationByName, type InheritedBase } from "../../data/derived";
import { isSameNetworkType, networkName, type FieldSending } from "../../data/network";
import { isConstructible, networkClassIds, polymorphicTypes } from "../../data/network-classes";
import { searchLink } from "../../utils/filtering";
import { formatFieldType, plural } from "../../utils/format";
import { formatRange } from "../../utils/console-format";
import { KindIcon } from "../kind-icon/KindIcon";
import { DeclarationsContext, fieldLink, schemaPath } from "./DeclarationsContext";
import { Detail } from "./Detail";
import { dimLink } from "./link-styles";
import { tip } from "../Tooltip";

/** In the row's notes, lined up with the field's name */
const NetworkLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 6px;
  font-size: 14px;
  color: var(--text-dim);

  a {
    ${dimLink}
  }
`;

/** One property, a dot between it and the one before */
const Attr = styled.span`
  &:not(:first-child)::before {
    content: "·";
    margin-right: 6px;
  }
`;

/** A little brighter than the labels, not as bright as the field names */
const Value = styled.span`
  font-size: 13px;
  color: color-mix(in srgb, var(--text) 55%, var(--text-dim));
`;

const Mark = styled.span`
  display: inline-flex;
  color: var(--text-dim);
`;

/** Next to a networked field's offset, crossed out when the class doesn't send it */
export function NetworkedMark({ sending }: { sending?: FieldSending }) {
  const off = sending?.sent === false;
  const text = off
    ? `Networked, but not sent by this class: ${sendingReason(sending)}`
    : `Networked, sent from the server to clients. ${sendingReason(sending)}`;

  return (
    <Mark {...tip(text.trim())} aria-label={off ? "not sent" : "networked"}>
      <KindIcon kind={off ? "meta-broadcast-off" : "meta-broadcast"} />
    </Mark>
  );
}

/** Which class decides whether it's sent, and how */
function sendingReason(sending: FieldSending | undefined) {
  if (!sending) return "";
  const { by, sent, group, noBase, via } = sending;
  if (via) return `It's kept for ${via}, which is sent.`;
  if (noBase) return `${by} sends none of its bases' fields it doesn't include.`;

  const verb = sent ? "includes" : "excludes";
  return group ? `${by} ${verb} its user group ${group}.` : `${by} ${verb} it.`;
}

/**
 * A name to search the network data for, like a user group or a callback. The search matches the
 * whole value, not every name that contains it, like LocalPlayerExclusive for Player
 */
function SearchValue({ value }: { value: string }) {
  const { game } = useContext(DeclarationsContext);
  return (
    <Link to={searchLink(game, `network:=${value}`)} title={`Find network data with ${value}`}>
      <Value>{value}</Value>
    </Link>
  );
}

function SearchValues({ values }: { values: string[] }) {
  return values.map((v, i) => (
    <React.Fragment key={v}>
      {i > 0 && ", "}
      <SearchValue value={v} />
    </React.Fragment>
  ));
}

/** A class named by the network data, linked when the game has it */
function ClassName({ name, module }: { name: string; module: string }) {
  const { game, declarations } = useContext(DeclarationsContext);
  const target = findDeclarationByName(declarations, name, "class", module);
  if (!target) return <Value>{name}</Value>;

  return (
    <Link to={schemaPath(game, target.module, target.name)} title={`class in ${target.module}`}>
      <Value>{name}</Value>
    </Link>
  );
}

/** The types a polymorphic pointer can hold, each after the id it's sent as */
function PolymorphicTypes({ field }: { field: SchemaField }) {
  const { references } = useContext(DeclarationsContext);

  return polymorphicTypes(references, field).map((type) => (
    <React.Fragment key={type.name}>
      {type.id > 0 && ", "}
      {type.id} <ClassName name={type.name} module={type.module} />
    </React.Fragment>
  ));
}

/**
 * The properties, shown as they are, in this order. Names that others search for link to a
 * search with the network: tag, and numbers and types are plain
 */
const FIELD_PROPERTIES: {
  key: keyof FieldNetwork;
  label: string;
  tip: string;
  search?: boolean;
}[] = [
  { key: "sentAs", label: "sent as", tip: "The type it's sent as." },
  { key: "alias", label: "alias", tip: "Name it's networked under." },
  { key: "resourceType", label: "resource", tip: "Resource type of the handle.", search: true },
  { key: "serializer", label: "serializer", tip: "Serializer.", search: true },
  { key: "encoder", label: "encoder", tip: "Encoder.", search: true },
  { key: "bitCount", label: "bits", tip: "Bits it's sent in, 32 when not given." },
  { key: "encodeFlags", label: "encode flags", tip: "Encoder flags." },
  // Not a send rate. Fields sort by it, so the ones that change often get small indices
  {
    key: "priority",
    label: "priority",
    tip: "Orders the class's fields, lowest first, 64 when not given.",
  },
  {
    key: "userGroups",
    label: "user group",
    tip: "User groups the field is in, classes include or exclude them.",
    search: true,
  },
  {
    key: "changeCallbacks",
    label: "on change",
    tip: "Called on the client when the value changes.",
    search: true,
  },
  {
    key: "changePointerCallback",
    label: "on pointer change",
    tip: "Called on the client when the pointer changes.",
    search: true,
  },
  { key: "recipientsFilter", label: "recipients", tip: "Filters who it's sent to.", search: true },
  {
    key: "embeddedFieldOffsetDelta",
    label: "embedded offset delta",
    tip: "Offset delta of the embedded field.",
  },
];

export type FieldAttr = { key: string; label: string; tip: string; value?: React.ReactNode };

/**
 * What to show of how a field is sent. A field sent as its own type with the defaults has nothing
 * to show. A property that names a class prefers the module of the declaring class
 */
export function fieldNetworkAttrs(field: SchemaField, module: string): FieldAttr[] {
  const network = field.network;
  if (!network) return [];

  const attrs: FieldAttr[] = [];
  const typeText = formatFieldType(field.type);

  if (!isSameNetworkType(typeText, network.type)) {
    attrs.push({
      key: "type",
      label: "as",
      tip: "The type it's networked as.",
      value: <Value>{network.type}</Value>,
    });
  }

  // Most name a class the field's type names already
  if (network.class && !typeText.split(/\W+/).includes(network.class)) {
    attrs.push({
      key: "class",
      label: "class",
      tip: "Class of the embedded, pointer or component field.",
      value: <ClassName name={network.class} module={module} />,
    });
  }

  if (network.polymorphic) {
    attrs.push({
      key: "polymorphic",
      label: "polymorphic",
      tip:
        "A polymorphic pointer, sent with the id of the type it holds: 0 for the field's class, " +
        "then its direct children in the same module in caseless order.",
      value: <PolymorphicTypes field={field} />,
    });
  }

  if (network.typeAlias && network.typeAlias !== network.alias) {
    attrs.push({
      key: "typeAlias",
      label: "type alias",
      tip: "Type name it's networked under.",
      value: <Value>{network.typeAlias}</Value>,
    });
  }

  for (const { key, label, tip, search } of FIELD_PROPERTIES) {
    const value = network[key];
    if (value == null || value === false) continue;

    const values = Array.isArray(value) ? value : [String(value)];
    attrs.push({
      key,
      label: values.length > 1 && key === "userGroups" ? "user groups" : label,
      tip,
      value: search ? <SearchValues values={values} /> : <Value>{values.join(", ")}</Value>,
    });
  }

  const range = formatRange(network.min?.toString(), network.max?.toString());

  if (range) {
    attrs.push({
      key: "range",
      label: "range",
      tip: "Range the encoding covers.",
      value: <Value>{range}</Value>,
    });
  }

  return attrs;
}

/** How a networked field is sent, under its name in the field row */
export function FieldNetworkLine({ attrs }: { attrs: FieldAttr[] }) {
  return (
    <NetworkLine>
      {attrs.map((a) => (
        <Attr key={a.key}>
          {/* On the label only, since a searchable value has a tooltip of its own */}
          <span {...tip(a.tip)}>{a.label}</span>
          {a.value != null && <> {a.value}</>}
        </Attr>
      ))}
    </NetworkLine>
  );
}

const NetworkList = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  align-items: baseline;
  gap: 4px 14px;
  font-size: 14px;

  a {
    ${dimLink}
  }

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 0;
  }
`;

const ListLabel = styled.span`
  color: var(--text-dim);

  @media (max-width: 768px) {
    &:not(:first-child) {
      margin-top: 6px;
    }
  }
`;

/** Names next to each other, or one entry per line */
const Items = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0 14px;
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--text-dim);

  &[data-lines] {
    flex-direction: column;
    gap: 2px;
  }
`;

/**
 * A field of the class or a base, by name or a path like m_baseLayer.m_hSequence, linked to the
 * nearest class that has its first part
 */
function FieldName({
  path,
  declaration,
  bases,
  className,
  showClass,
}: {
  path: string;
  declaration: SchemaClass;
  bases: InheritedBase[];
  /** The class that has the field, when it isn't the nearest */
  className?: string;
  /** Written before the field, the first of several fields of the class */
  showClass?: boolean;
}) {
  const { game, declarations } = useContext(DeclarationsContext);
  const first = path.split(".")[0];
  // The lists name fields by their network alias, like m_vecVelocity for m_vecServerVelocity
  const named = (f: SchemaField) => f.name === first || networkName(f) === first;
  let owner: { module: string; name: string } | undefined;
  let field: SchemaField | undefined;

  if (className) {
    // A||B::field names several classes. It links the first class that the game has
    for (const name of className.split("||")) {
      const cls = findDeclarationByName(declarations, name, "class", declaration.module);
      if (!cls) continue;

      owner = cls;
      if (cls.kind === "class") field = cls.fields.find(named);
      break;
    }
  } else {
    field = declaration.fields.find(named);
    if (field) owner = declaration;

    // The nearest base first
    for (let i = bases.length - 1; !field && i >= 0; i--) {
      field = bases[i].fields.find(named);
      if (field) owner = bases[i].parent;
    }
  }

  const fieldName = field?.name ?? first;

  // Long ones wrap after the class rather than inside a name
  const text =
    className && showClass ? (
      <>
        {className}::
        <wbr />
        {path}
      </>
    ) : (
      path
    );

  if (!owner) return <Value>{text}</Value>;

  return (
    <Link
      to={fieldLink(game, owner.module, owner.name, fieldName)}
      title={`${owner.name}::${fieldName}`}
    >
      <Value>{text}</Value>
    </Link>
  );
}

/**
 * The game's NetworkOverrideType_t. A user group override adds a group, and clears the field's
 * groups without a value
 */
const OVERRIDE_KINDS: Record<string, string> = {
  changeCallback: "on change",
  changeTag: "change tag",
  bitCount: "bits",
  userGroup: "user group",
  outOfPVSUpdates: "out of PVS updates",
  removeAll: "reset to defaults",
};

/** Overrides of the same class, kind and value on one line, like m_cellX, m_cellY and m_cellZ */
function groupOverrides(overrides: NetworkOverride[]) {
  const groups = new Map<string, { key: string; override: NetworkOverride; fields: string[] }>();

  for (const o of overrides) {
    const key = `${o.class}/${o.kind}/${o.value}`;
    const group = groups.get(key);

    if (group) {
      group.fields.push(o.field);
    } else {
      groups.set(key, { key, override: o, fields: [o.field] });
    }
  }

  return [...groups.values()];
}

/** Lists of names, each in a row of its own: field names or user groups */
const NAME_LISTS: {
  key:
    | "includeByName"
    | "excludeByName"
    | "includeByUserGroup"
    | "excludeByUserGroup"
    | "userGroupProxies";
  label: string;
  tip: string;
  fields?: boolean;
}[] = [
  { key: "includeByName", label: "Includes", tip: "Fields included by name.", fields: true },
  { key: "excludeByName", label: "Excludes", tip: "Fields excluded by name.", fields: true },
  { key: "includeByUserGroup", label: "Includes groups", tip: "User groups included." },
  { key: "excludeByUserGroup", label: "Excludes groups", tip: "User groups excluded." },
  { key: "userGroupProxies", label: "Group proxies", tip: "User group proxies." },
];

type Row = { label: string; tip: string; lines?: boolean; items: React.ReactNode };

/** Class-wide network settings, a details row of the class page */
export function ClassNetworkDetail({
  declaration,
  bases,
}: {
  declaration: SchemaClass;
  bases: InheritedBase[];
}) {
  const { declarations } = useContext(DeclarationsContext);
  if (!declaration.network) return null;

  // The class's own fields say on their rows whether it sends them. The lists name them by alias
  const own = new Set(declaration.fields.flatMap((f) => [f.name, networkName(f)]));
  const rows = networkRows(declaration, declaration.network, bases, own);
  if (declaration.network.networkClass) rows.unshift(networkClassRow(declarations, declaration));
  if (rows.length === 0) return null;

  return (
    <Detail label="Network">
      <RowList rows={rows} />
    </Detail>
  );
}

function RowList({ rows }: { rows: Row[] }) {
  return (
    <NetworkList>
      {rows.map((row) => (
        <React.Fragment key={row.label}>
          <ListLabel {...tip(row.tip)}>{row.label}</ListLabel>
          <Items data-lines={row.lines || undefined}>{row.items}</Items>
        </React.Fragment>
      ))}
    </NetworkList>
  );
}

const NETWORK_CLASS_TIP =
  "Registered with DECLARE_NETWORKCLASS. The server sends the constructible entity classes " +
  "among these in the class info of demos, with ids in the caseless order of their names.";

/** The class id the server sends the class with, or why it doesn't send it */
function networkClassRow(
  declarations: Map<string, Map<string, Declaration>>,
  declaration: SchemaClass,
): Row {
  const { ids, bits } = networkClassIds(declarations);
  const id = ids.get(declaration);

  if (id == null) {
    let reason = "not an entity class";
    if (!isConstructible(declaration)) {
      reason = declaration.flags.includes("abstract") ? "abstract" : "not constructible";
    }

    return {
      label: "Network class",
      tip: NETWORK_CLASS_TIP,
      items: <span>Not sent in the class info, {reason}</span>,
    };
  }

  return {
    label: "Network class",
    tip: NETWORK_CLASS_TIP,
    items: (
      <span>
        Class id <Value>{id}</Value> of {ids.size}, sent in {plural(bits, "bit")}
      </span>
    ),
  };
}

/** A row for each setting of the network data */
function networkRows(
  declaration: SchemaClass,
  network: ClassNetwork,
  bases: InheritedBase[],
  /** Field names left out of the lists by name */
  hidden?: Set<string>,
): Row[] {
  const field = (path: string, className?: string, showClass?: boolean) => (
    <FieldName
      key={path}
      path={path}
      declaration={declaration}
      bases={bases}
      className={className}
      showClass={showClass}
    />
  );
  const rows: Row[] = [];

  for (const { key, label, tip, fields } of NAME_LISTS) {
    let names = network[key];
    if (fields && hidden) names = names?.filter((name) => !hidden.has(name));
    if (!names?.length) continue;

    rows.push({
      label,
      tip,
      items: names.map((name) => {
        if (!fields) return <SearchValue key={name} value={name} />;

        // CGameSceneNode::m_hParent is the field in serializers of the class nested anywhere
        const colons = name.indexOf("::");
        return colons < 0
          ? field(name)
          : field(name.slice(colons + 2), name.slice(0, colons), true);
      }),
    });
  }

  if (network.overrides) {
    rows.push({
      label: "Overrides",
      tip: "Changes to how fields of this class or its bases are sent.",
      lines: true,
      items: groupOverrides(network.overrides).map(({ key, override: o, fields }) => {
        const noUserGroups = o.kind === "userGroup" && !o.value;
        const kindLabel = noUserGroups ? "no user groups" : (OVERRIDE_KINDS[o.kind] ?? o.kind);

        return (
          <span key={key}>
            {fields.map((f, i) => (
              <React.Fragment key={f}>
                {i > 0 && ", "}
                {field(f, o.class, i === 0)}
              </React.Fragment>
            ))}{" "}
            → {kindLabel}
            {/* Kinds without a value, like resetting, have an empty one */}
            {o.value && (
              <>
                {" "}
                <Value>{o.value}</Value>
              </>
            )}
          </span>
        );
      }),
    });
  }

  if (network.varTypeOverrides) {
    rows.push({
      label: "Type overrides",
      tip: "Fields sent as another type.",
      lines: true,
      items: Object.entries(network.varTypeOverrides).map(([f, type]) => (
        <span key={f}>
          {field(f)} → <ClassName name={type} module={declaration.module} />
        </span>
      )),
    });
  }

  if (network.replayCompatFields) {
    rows.push({
      label: "Replay compat",
      tip: "Field paths, as a regex, and their callback for replay compatibility.",
      lines: true,
      items: network.replayCompatFields.map((r) => (
        <span key={r.field}>
          <Value>{r.field}</Value> → <SearchValue value={r.callback} />
        </span>
      )),
    });
  }

  if (network.outOfPVSUpdates != null) {
    rows.push({
      label: "Out of PVS updates",
      tip: "Updates sent while out of the PVS, 2 when not given.",
      items: <Value>{network.outOfPVSUpdates}</Value>,
    });
  }

  const flags = [
    network.varsAtomic && "vars atomic",
    network.structNotInNetworkUtlVectorEmbedded && "not in embedded network vectors",
  ].filter(Boolean);

  if (flags.length > 0) {
    rows.push({
      label: "Flags",
      tip: "Settings of the class's network variables.",
      items: flags.join(", "),
    });
  }

  return rows;
}
